import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check } from "lucide-react";

export default function LanguageToggle({ lang, setLang, languages }) {
  const active = (languages || []).filter((l) => l.active);
  const primary = active.filter((l) => l.code === "en" || l.code === "it");
  const extras = active.filter((l) => l.code !== "en" && l.code !== "it");
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (code) => {
    setLang(code);
    setOpen(false);
  };

  return (
    <div ref={wrap} className="relative inline-flex items-center gap-1" data-testid="language-toggle">
      <div className="inline-flex items-center rounded-full border kt-line bg-white px-1 py-1 kt-shadow">
        {primary.map((l) => (
          <button
            key={l.code}
            data-testid={`lang-${l.code}`}
            onClick={() => pick(l.code)}
            className="relative px-3 py-1 text-[11px] kt-tracking uppercase"
          >
            {lang === l.code && (
              <motion.div
                layoutId="lang-pill"
                className="absolute inset-0 rounded-full bg-[#383633]"
                transition={{ type: "spring", stiffness: 360, damping: 34 }}
              />
            )}
            <span className={`relative ${lang === l.code ? "text-white" : "text-[#6F6B66]"}`}>
              {l.code.toUpperCase()}
            </span>
          </button>
        ))}
        {extras.length > 0 && (
          <>
            <span className="mx-1 h-4 w-px bg-[#ECE7DF]" />
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="lang-globe"
              aria-label="More languages"
              className={`px-2 py-1 rounded-full flex items-center gap-1 text-[#6F6B66] hover:text-[#383633] transition ${
                extras.some((l) => l.code === lang) ? "bg-[#F8F6F2]" : ""
              }`}
            >
              <Globe className="h-3.5 w-3.5" />
              {extras.some((l) => l.code === lang) && (
                <span className="text-[10px] kt-tracking uppercase text-[#383633]">{lang.toUpperCase()}</span>
              )}
            </button>
          </>
        )}
      </div>

      <AnimatePresence>
        {open && extras.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="absolute top-full right-0 mt-2 w-56 bg-white border kt-line rounded-2xl kt-shadow-md z-50 p-2"
            data-testid="lang-menu"
          >
            {extras.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-opt-${l.code}`}
                onClick={() => pick(l.code)}
                className="w-full flex items-center justify-between rounded-xl px-3 py-2 text-sm text-[#383633] hover:bg-[#F8F6F2] transition"
              >
                <span className="flex items-center gap-3">
                  <span className="text-xs kt-tracking uppercase text-[#6F6B66] w-8">{l.code.toUpperCase()}</span>
                  <span>{l.native || l.name}</span>
                  {l.rtl && <span className="text-[9px] kt-tracking uppercase text-[#A8AD94]">RTL</span>}
                </span>
                {lang === l.code && <Check className="h-4 w-4 text-[#383633]" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
